<template>
  <div class="content">
    <div class="content-header">
      <div class="chat-title">
        <div class="history-search-row">
          <el-input
            v-model="searchKeyword"
            class="history-search"
            size="mini"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索聊天记录"
            @focus="searchPanelOpen = true"
            @keyup.enter.native="searchMessages"
            @clear="clearSearch"
          />
          <el-button
            size="mini"
            type="primary"
            icon="el-icon-search"
            :loading="searchLoading"
            @click="searchMessages"
          >搜索</el-button>
        </div>
        <div class="chat-name">{{ info.project_code || info.project_product }}</div>
        <div class="school-name">{{ info.account_name }}</div>
      </div>
      <div class="header-tools">
        <img class="group-icon" src="../../assets/group.png" @click="showGroupInfo" />
      </div>
    </div>
    <div class="content-body">
      <div class="content-main" ref="chatRef" @scroll="onScroll">
        <div class="main">
          <div
            v-for="(item, index) in messageList"
            ref="messageItems"
            class="message"
            :class="{ 'active-search-message': activeSearchMessageKey === messageKey(item) }"
            :key="messageKey(item) || index"
            :data-message-key="messageKey(item)"
          >
            <div class="sender-item" v-if="item.message_type === 'web'">
              <el-avatar shape="square" :size="40" :src="messageAvatar(item)"></el-avatar>
              <div style="flex: 1;">
                <div class="info">
                  <div class="name">{{ senderName(item) }}</div>
                  <div class="name">
                    {{ $dayjs(item.create_time * 1000).format("MM-DD HH:mm") }}
                  </div>
                </div>
                <div class="sender-content">{{ item.message_content }}</div>
              </div>
            </div>
            <div class="receiver-item" v-else>
              <el-avatar shape="square" :size="40" :src="messageAvatar(item)"></el-avatar>
              <div style="flex: 1;">
                <div class="info">
                  <div class="name">{{ senderName(item) }}</div>
                  <div class="name">
                    {{ $dayjs(item.create_time * 1000).format("MM-DD HH:mm") }}
                  </div>
                </div>
                <div class="receiver-content">{{ item.message_content }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="searchPanelVisible" class="search-panel">
        <div class="search-panel-header">
          <div>
            <div class="search-panel-title">聊天记录</div>
            <div class="search-panel-count" v-if="trimmedSearchKeyword">
              {{ searchLoading ? '搜索中...' : `共 ${searchResults.length} 条` }}
            </div>
          </div>
          <i class="el-icon-close search-close" @click="closeSearchPanel"></i>
        </div>
        <div class="search-panel-body">
          <div v-if="searchLoading" class="search-placeholder">
            正在匹配历史记录...
          </div>
          <div v-else-if="!trimmedSearchKeyword" class="search-placeholder">
            输入关键词后按 Enter 搜索
          </div>
          <div v-else-if="searchResults.length === 0" class="search-placeholder">
            未找到匹配记录
          </div>
          <template v-else>
            <div
              v-for="item in searchResults"
              :key="`search-${messageKey(item)}`"
              class="search-result"
              :class="{ active: activeSearchMessageKey === messageKey(item) }"
              @click="locateSearchResult(item)"
            >
              <div class="search-result-meta">
                <span>{{ senderName(item) }}</span>
                <span>{{ $dayjs(item.create_time * 1000).format("MM-DD HH:mm") }}</span>
              </div>
              <div class="search-result-content" v-html="highlightKeyword(searchSnippet(item.message_content))"></div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getMessageDetail, searchContactReachMessages } from '../../api/assistant'
import { getUserId } from '../../utils/store'

export default {
  name: 'MessageContent',
  props: {
    info: {
      type: Object
    }
  },
  data () {
    return {
      squareUrl:
        'http://mainbucket.reachable-edu.com/hEaIyvBOHDTV0cd6468c61f7992cd9ef0aa688424c55.jpeg',
      messageList: [],
      userId: getUserId(),
      loadingOlder: false,
      loadedAllMessages: false,
      searchKeyword: '',
      searchResults: [],
      searchLoading: false,
      searchPanelOpen: false,
      activeSearchMessageKey: '',
      searchRequestIndex: 0
    }
  },
  computed: {
    trimmedSearchKeyword () {
      return this.searchKeyword.trim()
    },
    searchPanelVisible () {
      return this.searchPanelOpen || this.searchLoading || this.searchResults.length > 0
    }
  },
  mounted () {
    this.getChatDetail()
  },
  watch: {
    info () {
      this.resetSearch()
      this.getChatDetail()
    }
  },
  methods: {
    buildMessageParams (createTime) {
      return {
        account_id: this.info.account_id,
        group_id: this.info._id.$id,
        get_type: 'web',
        user_id: getUserId(),
        create_time: createTime
      }
    },
    async fetchMessagePage (createTime) {
      const res = await getMessageDetail(this.buildMessageParams(createTime))
      if (res.data.code !== 0 || !Array.isArray(res.data.data)) {
        return []
      }
      return res.data.data.sort((a, b) => a.create_time - b.create_time)
    },
    async fetchSearchMessages (keyword) {
      const res = await searchContactReachMessages({
        account_id: this.info.account_id,
        group_id: this.info._id.$id,
        get_type: 'web',
        user_id: getUserId(),
        search_word: keyword,
        limit: 50
      })
      if (res.data.code !== 0 || !Array.isArray(res.data.data)) {
        return []
      }
      return res.data.data
    },
    async getChatDetail () {
      const messages = await this.fetchMessagePage(Date.now())
      if (messages.length >= 0) {
        if (this.searchPanelVisible && this.messageList.length > 0) {
          this.mergeMessages(messages)
        } else {
          this.messageList = this.uniqueMessages(messages)
        }
        this.loadedAllMessages = messages.length === 0
        if (!this.searchPanelVisible) {
          this.$nextTick(() => this.scrollToBottom())
        }
      }
    },
    scrollToBottom () {
      const el = this.$refs.messageItems
      if (el && el.length > 0) {
        el[el.length - 1].scrollIntoView({ behavior: 'smooth' })
      }
    },
    showGroupInfo () {
      this.$emit('show')
    },
    async onScroll (e) {
      if (e.target.scrollTop !== 0 || this.loadingOlder || this.loadedAllMessages || this.messageList.length === 0) {
        return
      }
      this.loadingOlder = true
      try {
        const messages = await this.fetchMessagePage(this.messageList[0].create_time)
        if (messages.length > 0) {
          this.messageList = this.uniqueMessages([...messages, ...this.messageList])
          this.$refs.chatRef.scrollTop = 60
        } else {
          this.loadedAllMessages = true
        }
      } finally {
        this.loadingOlder = false
      }
    },
    uniqueMessages (messages) {
      const map = new Map()
      messages.forEach(item => {
        const key = this.messageKey(item)
        if (!key || map.has(key)) return
        map.set(key, item)
      })
      return [...map.values()].sort((a, b) => a.create_time - b.create_time)
    },
    mergeMessages (messages) {
      this.messageList = this.uniqueMessages([...this.messageList, ...messages])
    },
    messageKey (message) {
      if (!message) return ''
      return (
        message.message_id ||
        (message._id && (message._id.$id || message._id.$oid)) ||
        message.id ||
        `${message.create_time || ''}-${message.message_type || ''}-${message.message_content || ''}`
      )
    },
    messageAvatar (message) {
      return (message.sender_info && message.sender_info.headPicUrl) || this.squareUrl
    },
    senderName (message) {
      const info = message.sender_info || {}
      if (message.message_type === 'web') {
        return info.user_enName || info.user_name || '我'
      }
      return info.contact_enName || info.contact_name || info.user_enName || '对方'
    },
    resetSearch () {
      this.searchRequestIndex += 1
      this.searchKeyword = ''
      this.searchResults = []
      this.searchLoading = false
      this.searchPanelOpen = false
      this.activeSearchMessageKey = ''
    },
    clearSearch () {
      this.searchResults = []
      this.activeSearchMessageKey = ''
    },
    closeSearchPanel () {
      this.searchPanelOpen = false
      this.activeSearchMessageKey = ''
    },
    async searchMessages () {
      const keyword = this.trimmedSearchKeyword
      this.searchPanelOpen = true
      this.activeSearchMessageKey = ''
      if (!keyword) {
        this.searchResults = []
        return
      }
      const requestIndex = this.searchRequestIndex + 1
      this.searchRequestIndex = requestIndex
      this.searchLoading = true
      try {
        const messages = await this.fetchSearchMessages(keyword)
        if (requestIndex !== this.searchRequestIndex) return
        this.mergeMessages(messages)
        this.searchResults = messages.sort((a, b) => b.create_time - a.create_time)
      } catch (error) {
        console.log(error)
        this.$message.error('聊天记录搜索失败，请稍后重试')
      } finally {
        if (requestIndex === this.searchRequestIndex) {
          this.searchLoading = false
        }
      }
    },
    locateSearchResult (message) {
      this.activeSearchMessageKey = this.messageKey(message)
      this.$nextTick(() => {
        const items = this.$refs.messageItems || []
        const target = items.find(item => item.dataset.messageKey === this.activeSearchMessageKey)
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    escapeHtml (text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    escapeRegExp (text) {
      return String(text).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    },
    searchSnippet (content) {
      const text = String(content || '')
      const keyword = this.trimmedSearchKeyword.toLowerCase()
      const index = text.toLowerCase().indexOf(keyword)
      if (index === -1 || text.length <= 90) return text
      const start = Math.max(index - 28, 0)
      const end = Math.min(index + keyword.length + 52, text.length)
      return `${start > 0 ? '...' : ''}${text.slice(start, end)}${end < text.length ? '...' : ''}`
    },
    highlightKeyword (content) {
      const text = this.escapeHtml(content)
      const keyword = this.trimmedSearchKeyword
      if (!keyword) return text
      return text.replace(new RegExp(`(${this.escapeRegExp(keyword)})`, 'ig'), '<mark>$1</mark>')
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  height: 100%;
  display: flex;
  flex-direction: column;
  .content-header {
    padding: 18px;
    border-bottom: 2px solid #f2f2f2;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    .chat-title {
      flex: 1;
      min-width: 0;
    }
    .chat-name {
      margin-top: 10px;
    }
    .school-name {
      font-size: 12px;
      color: #999;
      margin-top: 8px;
    }
    .header-tools {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-shrink: 0;
    }
    .history-search-row {
      display: flex;
      gap: 8px;
      align-items: center;
    }
    .history-search {
      width: 220px;
    }
    .group-icon {
      width: 28px;
      height: 22px;
      cursor: pointer;
      margin-left: 6px;
    }
  }
  .content-body {
    flex: 1;
    min-height: 0;
    display: flex;
  }
  .main {
    padding: 18px;
  }

  .content-main {
    flex: 1;
    min-width: 0;
    height: 100%;
    overflow-y: scroll;
  }
  .search-panel {
    width: 300px;
    height: 100%;
    border-left: 1px solid #f0f0f0;
    background: #fafafa;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    .search-panel-header {
      height: 58px;
      padding: 12px 14px;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      justify-content: space-between;
      border-bottom: 1px solid #eeeeee;
    }
    .search-panel-title {
      color: #333333;
      font-size: 14px;
      line-height: 18px;
    }
    .search-panel-count {
      color: #999999;
      font-size: 12px;
      margin-top: 4px;
    }
    .search-close {
      color: #999999;
      cursor: pointer;
      font-size: 16px;
    }
    .search-panel-body {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 10px;
      box-sizing: border-box;
    }
    .search-placeholder {
      color: #999999;
      font-size: 13px;
      text-align: center;
      padding-top: 42px;
    }
    .search-result {
      padding: 12px;
      border-radius: 6px;
      background: #ffffff;
      border: 1px solid #eeeeee;
      cursor: pointer;
      margin-bottom: 10px;
      transition: border-color 0.2s, background 0.2s;
      &.active,
      &:hover {
        border-color: #508cff;
        background: #f3f7ff;
      }
    }
    .search-result-meta {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      color: #999999;
      font-size: 12px;
      margin-bottom: 8px;
      span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
    .search-result-content {
      color: #333333;
      font-size: 13px;
      line-height: 20px;
      word-break: break-word;
      ::v-deep mark {
        background: #fff1a8;
        color: #333333;
        padding: 0 2px;
      }
    }
  }
  .message {
    border-radius: 6px;
    transition: background 0.2s;
    &.active-search-message {
      background: #fff8df;
    }
  }

  .receiver-item {
    display: flex;
    margin-bottom: 18px;
    .info {
      display: flex;
      margin-bottom: 2px;
    }
    .name {
      color: #999;
      font-size: 12px;
      margin-left: 16px;
    }
    .receiver-content {
      background: #f5f5f5;
      height: fit-content;
      border-radius: 10px;
      padding: 16px;
      word-break: break-word;
      max-width: 600px;
      margin-left: 16px;
      width: fit-content;
    }
  }
  .sender-item {
    display: flex;
    flex-direction: row-reverse;
    margin-bottom: 18px;
    .info {
      display: flex;
      flex-direction: row-reverse;
      margin-bottom: 2px;
    }
    .name {
      color: #999;
      font-size: 12px;
      margin-right: 16px;
    }
    .sender-content {
      background: #508cff;
      color: white;
      height: fit-content;
      border-radius: 10px;
      padding: 16px;
      word-break: break-word;
      max-width: 600px;
      width: fit-content;
      margin-right: 16px;
      float: right;
    }
  }
}
</style>
